import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "@/components/ui/input";
import ImageUploadPreview from "../image-upload-preview";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type {
	ApiPage,
	ApiPageField,
	Brandkit,
	FormBrand,
} from "../../types/api.types";
import {
	buildFieldSchema,
	type DynamicFormData,
} from "../../utils/field-helpers";

interface StepAboutUserProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	totalSteps: number;
	currentStepIndex: number;
	isDarkMode?: boolean;
}

export default function StepAboutUser({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	totalSteps,
	currentStepIndex,
	isDarkMode,
}: StepAboutUserProps) {
	const {
		data,
		setUserInfo,
		nextStep,
		updateUserInfoStep,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const visibleFields = useMemo(
		() =>
			(pageConfig.fields ?? [])
				.filter((f) => f.is_visible)
				.sort((a, b) => a.sort_order - b.sort_order),
		[pageConfig.fields],
	);

	const schema = useMemo(() => {
		const shape: Record<string, z.ZodTypeAny> = {};
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				shape[field.field_key] = buildFieldSchema(field);
			}
		}
		return z.object(shape);
	}, [visibleFields]);

	const defaultValues = useMemo(() => {
		const values: Record<string, string> = {};
		const userInfo = data.userInfo as unknown as Record<string, unknown>;
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				values[field.field_key] =
					(userInfo[field.field_key] as string) ?? "";
			}
		}
		return values;
	}, [visibleFields, data.userInfo]);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<DynamicFormData>({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: zodResolver(schema) as any,
		mode: "onChange",
		defaultValues,
	});

	const onSubmit = async (formData: DynamicFormData) => {
		const userInfoUpdate: Record<string, string> = {};
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				userInfoUpdate[field.field_key] =
					formData[field.field_key] || "";
			}
		}
		setUserInfo(userInfoUpdate);

		try {
			setError(null);
			await updateUserInfoStep();
			nextStep();
		} catch (err) {
			setError(
				submitError ||
					"Failed to save your information. Please try again.",
			);
		}
	};

	const handlePhotoChange = (file: File | null, preview: string | null) => {
		setUserInfo({
			photoFile: file,
			photoPreview: preview,
		});
	};

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	const renderField = (field: ApiPageField) => {
		if (field.field_type === "file") {
			return (
				<div
					key={field.field_key}
					className="grid grid-cols-[25%_1fr] gap-4 items-stretch"
				>
					<ImageUploadPreview
						label={field.label}
						preview={data.userInfo.photoPreview}
						onChange={handlePhotoChange}
						icon="user"
						isDark={isDarkMode}
					/>
				</div>
			);
		}

		const inputType =
			field.field_type === "tel"
				? "tel"
				: field.field_type === "email"
					? "email"
					: field.field_type === "url"
						? "url"
						: "text";

		return (
			<Input
				key={field.field_key}
				label={field.label}
				type={inputType}
				placeholder={field.placeholder ?? undefined}
				required={field.is_required}
				error={errors[field.field_key]?.message as string | undefined}
				{...register(field.field_key)}
				isDark={isDarkMode}
			/>
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="contents">
			<BoldLayout
				showBranding={brand?.branding !== "disabled"}
				brandName={brand.name}
				brandLogo={brandLogo}
				brandkit={brandkit}
				stepKey={stepKey}
				showBackButton={showBackButton}
				onBack={onBack}
				title={pageConfig.attributes.title}
				description={pageConfig.attributes.description}
				progress={{ current: currentStepIndex + 1, total: totalSteps }}
				isFormSubmit
				continueDisabled={!isValid || isSubmitting}
				continueLabel={isSubmitting ? "Saving..." : "Continue"}
				centered={false}
				isDarkMode={isDarkMode}
			>
				<div className="space-y-8">
					{visibleFields.map(renderField)}
				</div>

				{error && (
					<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
						<p className="text-sm text-red-600">{error}</p>
						<button
							type="submit"
							className="mt-2 text-sm text-red-700 underline hover:no-underline"
						>
							Try Again
						</button>
					</div>
				)}
			</BoldLayout>
		</form>
	);
}
